import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import apiClient from '@/services/api';
import type { FoodDrinkReport, FoodDrinkDayRow } from '@/types/api';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

/**
 * Food vs Drink — per-weekday matrix matching the POS report exactly:
 * food/drink counts & amounts and discount count & amount per weekday, plus a
 * grand-total row (Total Value = Food + Drink amount − Discount).
 *
 * Include/exclude checkboxes (Food / Drinks / Discounts, all on by default)
 * mirror the POS FoodDrinkReportView toggles: unchecking a box removes that
 * category's columns and drops it from the Total Items / Total Value figures.
 * "Make Total" mirrors the POS checkbox of the same name: checking it collapses
 * the per-weekday breakdown down to the single grand-total row.
 * These are display-only — the DTO already carries every category and the
 * pre-computed total row, so toggling never refetches.
 */
export default function FoodVsDrinkReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;
  const [includeFood, setIncludeFood] = useState(true);
  const [includeDrink, setIncludeDrink] = useState(true);
  const [includeDiscount, setIncludeDiscount] = useState(true);
  const [makeTotal, setMakeTotal] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery<FoodDrinkReport>({
    queryKey: ['food-drink', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getFoodDrinkReport(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const days = data?.days ?? [];
  const total = data?.total;
  const num = (n: number) => Number(n ?? 0).toLocaleString();
  const isEmpty = !total || Number(total?.totalItems ?? 0) === 0;

  // Headers follow the checkbox selection: excluded categories drop their columns (POS parity).
  const headers = [
    'Day',
    ...(includeFood ? ['Food Qty', 'Food Amount'] : []),
    ...(includeDrink ? ['Drink Qty', 'Drink Amount'] : []),
    ...(includeDiscount ? ['Disc. Count', 'Disc. Amount'] : []),
    'Total Items', 'Total Value',
  ];

  const rowFor = (r: FoodDrinkDayRow): (string | number)[] => {
    // Recompute totals from the included categories only — do not trust the DTO's
    // precomputed totalItems/totalValue, which always assume all three are in.
    const totalItems = (includeFood ? Number(r.foodCount ?? 0) : 0) + (includeDrink ? Number(r.drinkCount ?? 0) : 0);
    const totalValue = (includeFood ? Number(r.foodAmount ?? 0) : 0)
      + (includeDrink ? Number(r.drinkAmount ?? 0) : 0)
      - (includeDiscount ? Number(r.discountAmount ?? 0) : 0);
    return [
      r.day,
      ...(includeFood ? [num(r.foodCount), formatCurrency(r.foodAmount)] : []),
      ...(includeDrink ? [num(r.drinkCount), formatCurrency(r.drinkAmount)] : []),
      ...(includeDiscount ? [num(r.discountCount), formatCurrency(r.discountAmount)] : []),
      num(totalItems), formatCurrency(totalValue),
    ];
  };

  const bodyRows: (string | number)[][] = makeTotal
    ? (total ? [rowFor(total)] : [])
    : [...days.map(rowFor), ...(total ? [rowFor(total)] : [])];

  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });
  const sections = (): ReportSection[] => [{ heading: 'Food vs Drink', headers, rows: bodyRows }];

  const toggle = (label: string, checked: boolean, onChange: (v: boolean) => void) => (
    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );

  return (
    <ReportFrame
      title="Food vs Drink"
      description="Food and drink counts & amounts and discounts, broken down by day of week (matches the POS report)."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={isEmpty}
      exportDisabled={!data}
      onExportCsv={() => data && downloadCsv(`food-vs-drink-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildGenericCsv('Food vs Drink', meta(), [], sections()))}
      onExportPdf={() => data && printGenericReport('Food vs Drink Report', meta(), [], sections())}
    >
      <div className="card"><div className="card-body">
        <div className="flex items-center gap-6 mb-4">
          <span className="text-sm font-medium text-gray-500">Include:</span>
          {toggle('Food', includeFood, setIncludeFood)}
          {toggle('Drinks', includeDrink, setIncludeDrink)}
          {toggle('Discounts', includeDiscount, setIncludeDiscount)}
          <span className="border-l pl-6">{toggle('Make Total', makeTotal, setMakeTotal)}</span>
        </div>
        <SimpleTable headers={headers} rows={bodyRows} />
      </div></div>
    </ReportFrame>
  );
}
